import { FC, useCallback, useMemo } from 'react';
import {
  ButtonGroupNav,
  ButtonGroupNavItem,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { expand } from 'uri-template-lite';
import { useSectionContext } from '../../../common/SectionContext';
import { ErrorReportType, ErrorSegment } from '../types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './OutputDataProcessingErrorReportButtonNav.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'output-data-processing-error-report-segments',
);
addTranslations(translations);

export interface OutputDataProcessingErrorReportButtonNavProps {
  type: ErrorReportType;
  id: number;
  processingDataObjectId?: number;
  descriptionId?: number;
  counts?: Record<ErrorSegment, number>;
}

export const OutputDataProcessingErrorReportButtonNav: FC<
  OutputDataProcessingErrorReportButtonNavProps
> = (props) => {
  const { type, id, processingDataObjectId, descriptionId, counts } = props;
  const { t } = useTranslation();
  const { basePath } = useSectionContext();

  const expandUrlTemplate = useCallback(
    (errorSegment: ErrorSegment) =>
      expand(
        `${basePath}/errors/{type}/{id}{?errorSegment,processingDataObjectId,descriptionId}`,
        {
          type,
          id,
          processingDataObjectId,
          descriptionId,
          errorSegment,
        },
      ),
    [basePath, type, id, processingDataObjectId, descriptionId],
  );

  const items: ButtonGroupNavItem<ErrorSegment>[] = useMemo(
    () => [
      {
        key: 'calculationErrors',
        label:
          t('segment.calculationErrors') +
          (counts?.calculationErrors ? ` (${counts.calculationErrors})` : ''),
        to: expandUrlTemplate('calculationErrors'),
      },
      {
        key: 'validationErrors',
        label:
          t('segment.validationErrors') +
          (counts?.validationErrors ? ` (${counts.validationErrors})` : ''),

        to: expandUrlTemplate('validationErrors'),
      },
      {
        key: 'missingSourceData',
        label:
          t('segment.missingSourceData') +
          (counts?.missingSourceData ? ` (${counts.missingSourceData})` : ''),
        to: expandUrlTemplate('missingSourceData'),
      },
    ],
    [t, expandUrlTemplate, counts],
  );

  const [{ errorSegment }] = useQueryParams();

  return <ButtonGroupNav items={items} currentKey={errorSegment} />;
};
